import { Component } from 'preact';
import {
  ACCENT_GOLD,
  CONTENT_BG_DEFAULT,
  TEXT_BODY,
  TEXT_MUTED,
  TEXT_PRIMARY,
} from '../../lib/medievalTheme';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { HERO_BY_KEY } from './ModeHeroArt';
import { Focusable } from '../ui/Focusable';
import { t } from '../../localization/t';

function Badge({ label, value }) {
  return (
    <div
      className="px-2.5 py-1 rounded-md flex flex-col"
      style={{
        background: 'rgba(12, 10, 8, 0.78)',
        border: '1px solid rgba(180,140,60,0.22)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <span className="text-[0.5625cqw] font-semibold" style={{ color: TEXT_MUTED }}>
        {t(label)}
      </span>
      <span className="text-[0.75cqw] font-semibold" style={{ color: TEXT_PRIMARY }}>
        {t(value)}
      </span>
    </div>
  );
}

function LockGlyph() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export default class ModeTile extends Component {
  state = { hovered: false, artFailed: false };

  // Ambient mode (touch screens): there is no hover, so the tile plays
  // its hover/idle art variants on a slow self-timer instead. Jittered
  // dwell times + a per-tile starting phase keep the grid desynced so
  // the motion reads as each scene breathing on its own, not as a
  // synchronized hover effect.
  componentDidMount() {
    if (this.props.ambient) {
      const phase = this.props.ambientPhase || 0;
      this._ambientTimer = setTimeout(this._ambientTick, 1100 + phase * 1300 + Math.random() * 700);
    }
  }

  componentWillUnmount() {
    clearTimeout(this._ambientTimer);
  }

  _ambientTick = () => {
    const next = !this.state.hovered;
    this.setState({ hovered: next });
    const dwell = next
      ? 2200 + Math.random() * 1200
      : 3200 + Math.random() * 2400;
    this._ambientTimer = setTimeout(this._ambientTick, dwell);
  };

  handleArtError = () => {
    this.setState({ artFailed: true });
  };

  handleClick = () => {
    const { available, onClick } = this.props;
    if (!available || !onClick) return;
    playUI(UI.CONFIRM);
    onClick();
  };

  handleMouseEnter = () => {
    this.setState({ hovered: true });
    this.props.onHoverChange?.(true);
  };

  handleMouseLeave = () => {
    this.setState({ hovered: false });
    this.props.onHoverChange?.(false);
  };

  render() {
    const { mode, badges, available, reason, sorceryCards } = this.props;
    const { hovered, artFailed } = this.state;
    const tileId = `mode-tile-${mode.key}`;

    return (
      <Focusable id={tileId} asChild onActivate={this.handleClick}>
        <button
          type="button"
          onClick={this.handleClick}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
          disabled={!available}
          aria-pressed={this.props.selected === undefined ? undefined : !!this.props.selected}
          className={`vh-mode-tile${this.props.ambient ? ' vh-mode-tile--ambient' : ''}${this.props.compact ? ' vh-mode-tile--compact' : ''}${this.props.selected === undefined ? '' : (this.props.selected ? ' vh-mode-tile--selected' : ' vh-mode-tile--unselected')}`}
        >
          <div className="vh-mode-tile-art" aria-hidden>
          {/* Background art. Prefers the per-mode JSX hero component
              (ModeHeroArt). Raster fallback for modes that still ship a
              heroArt URL and haven't been ported to a hero component. */}
          {(() => {
            const HeroComponent = HERO_BY_KEY[mode.key];
            if (HeroComponent) return <HeroComponent sorceryCards={sorceryCards} hovered={hovered && available} />;
            if (mode.heroArt && !artFailed) {
              return (
                <img
                  src={mode.heroArt}
                  alt=""
                  draggable={false}
                  onError={this.handleArtError}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              );
            }
            return (
              <div
                className="absolute inset-0"
                style={{
                  background: `${CONTENT_BG_DEFAULT}, linear-gradient(135deg, rgba(35,25,12,0.9), rgba(12,10,8,0.95))`,
                }}
              />
            );
          })()}

          {/* Legibility gradient. Darkens the bottom where the title
              sits so the art never fights the text. */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, rgba(10,8,6,0.35) 0%, rgba(10,8,6,0.1) 30%, rgba(10,8,6,0.88) 100%)',
            }}
          />

          {/* Warm ember bloom. Fades in on hover via CSS; breathes on a
              loop in ambient mode (phase-offset per tile). */}
          <div className="vh-mode-tile-ember" style={this.props.ambient ? { animationDelay: `${(this.props.ambientPhase || 0) * 1.4}s` } : undefined} />
          {/* Top-edge sheen. Subtle polished-brass highlight. */}
          <div className="vh-mode-tile-sheen" style={this.props.ambient ? { animationDelay: `${(this.props.ambientPhase || 0) * 1.4}s` } : undefined} />
          </div>

          {/* Badges */}
          {available && badges.length > 0 ? (
            <div className="absolute top-3 right-3 flex flex-wrap gap-1.5 justify-end max-w-[60%]" style={{ zIndex: 4 }}>
              {badges.map((badge) => (
                <Badge key={badge.label} label={badge.label} value={badge.value} />
              ))}
            </div>
          ) : null}

          {/* Title + tagline. Text is engraved via dual text-shadow.
              a deep dark halo for legibility against varied hero art,
              plus a warm lower highlight that reads as carved gold. */}
          <div className="absolute left-5 right-5 bottom-4 flex flex-col gap-1.5" style={{ zIndex: 4 }}>
            <div className="flex items-center gap-2">
              {!available ? (
                <span style={{ color: ACCENT_GOLD }}>
                  <LockGlyph />
                </span>
              ) : null}
              <h3
                className="arena-heading text-2xl font-bold leading-tight tracking-wide"
                style={{
                  color: TEXT_PRIMARY,
                  textShadow: '0 -1px 0 rgba(0,0,0,0.8), 0 1px 0 rgba(255,220,140,0.18), 0 2px 6px rgba(0,0,0,0.9), 0 0 16px rgba(0,0,0,0.6)',
                }}
              >
                {t(mode.title)}
              </h3>
            </div>
            <p
              className="text-sm leading-5"
              style={{
                color: available ? TEXT_BODY : ACCENT_GOLD,
                textShadow: '0 1px 3px rgba(0,0,0,0.85)',
                opacity: available ? 0.95 : 1,
              }}
            >
              {available ? t(mode.tagline) : (reason || t('Not available'))}
            </p>
          </div>
        </button>
      </Focusable>
    );
  }
}
